/**
 * Retained roster versions, fetched by id when they are needed and kept.
 *
 * The state lists versions as headers only (HistoryEntry); a version's grid is needed
 * only to look at it, compare it, or undo onto it. A version never changes — its id is
 * a hash of its content — so one fetched (or seen as the current version) is good for
 * the rest of the page's life. Every current version the tab is shown is remembered,
 * which is what lets undo jump back at once without asking the server.
 *
 * Bounded (LRU): the server keeps at most ~90 versions per roster, so this holds a
 * roster's worth, not a day's.
 */
import { useEffect, useSyncExternalStore } from 'react';

import { apiClient } from '@/api/client';
import type { HistorySnapshot } from '@/types/api';

const MAX_VERSIONS = 120;
const versions = new Map<string, HistorySnapshot>();
const inFlight = new Map<string, Promise<HistorySnapshot | null>>();
const listeners = new Set<() => void>();
let generation = 0;

function notify(): void {
  generation += 1;
  listeners.forEach((l) => l());
}

/** Keep a version the tab already holds (one shown as current, or fetched). */
export function rememberVersion(snapshot: HistorySnapshot | null | undefined): void {
  const vid = snapshot?.vid;
  if (!vid) return;
  if (versions.get(vid) === snapshot) return;
  versions.delete(vid);
  versions.set(vid, snapshot);
  while (versions.size > MAX_VERSIONS) versions.delete(versions.keys().next().value as string);
  notify();
}

/** The version, if this tab has it. */
export function cachedVersion(vid: string | null | undefined): HistorySnapshot | undefined {
  return vid ? versions.get(vid) : undefined;
}

/** The version, from the cache or the server (one request however many ask at once). */
export function loadVersion(vid: string): Promise<HistorySnapshot | null> {
  const have = versions.get(vid);
  if (have) return Promise.resolve(have);
  let pending = inFlight.get(vid);
  if (!pending) {
    pending = apiClient.getVersion(vid)
      .then((snapshot) => { rememberVersion(snapshot); return snapshot; })
      .catch(() => null)
      .finally(() => { inFlight.delete(vid); });
    inFlight.set(vid, pending);
  }
  return pending;
}

/** Re-render when versions arrive. */
function useVersionStoreGeneration(): number {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => generation,
  );
}

/**
 * The versions with these ids — each undefined until it has arrived. Fetches what is
 * missing; components re-render as each lands.
 */
export function useVersions(vids: Array<string | null | undefined>): Array<HistorySnapshot | undefined> {
  useVersionStoreGeneration();
  const key = vids.join('|');
  useEffect(() => {
    for (const vid of vids) {
      if (vid && !versions.has(vid)) void loadVersion(vid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return vids.map((vid) => cachedVersion(vid));
}
